import { useState, useEffect } from 'react';
import { useSettings } from '../../context/SettingsContext';
import en from '../../locales/en.json';
import hi from '../../locales/hi.json';
import gu from '../../locales/gu.json';

const translations = { en, hi, gu };

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 768 : false
  );
  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return isMobile;
}

const isActionColumn = (header) => {
  if (!header || typeof header !== 'string') return false;
  const clean = header.trim().toLowerCase();
  return (
    clean === 'action' ||
    clean === 'actions' ||
    clean === 'col_actions' ||
    clean === 'col_action' ||
    /^(actions?|ક્રિયાઓ|કાર્યવાહી|કાર્રવાઈ|कार्रवाई|कार्रवाइयां|क्रियाएँ)$/i.test(clean)
  );
};

export default function DataTable({ cols, rows = [], emptyMsg, compact = false, footer, mobileCardRender, loading = false, horizontalScroll = true }) {
  const { language = 'en' } = useSettings();
  const t = (key, fallback) => translations[language]?.common?.[key] || translations.en?.common?.[key] || fallback;
  const displayEmptyMsg = emptyMsg || t("msg_no_records_found", "No records found.");
  const isMobile = useIsMobile();
  const pad = compact ? "px-3 py-2" : "px-3.5 py-3";

  if (isMobile) {
    if (rows.length === 0) {
      return <div className="py-12 text-center text-muted dark:text-slate-400 text-sm">{displayEmptyMsg}</div>;
    }
    return (
      <div className="space-y-3">
        {mobileCardRender
          ? rows.map((row, i) => (
              <div key={i} className="px-4 py-3 border border-slate-200 dark:border-slate-700/60 rounded-xl bg-slate-50/30 dark:bg-slate-800/20 shadow-sm">
                {mobileCardRender(row, i)}
              </div>
            ))
          : rows.map((row, i) => (
              <div key={i} className="px-4 py-3 border border-slate-200 dark:border-slate-700/60 rounded-xl divide-y divide-slate-100 dark:divide-slate-700/50 bg-slate-50/30 dark:bg-slate-800/20 shadow-sm">
                {row.map((cell, j) => {
                  const isAction = isActionColumn(cols[j]);
                  if (isAction) {
                    return (
                      <div key={j} className="pt-3 pb-1 first:pt-0 last:pb-0 flex items-center justify-end gap-2 flex-wrap w-full">
                        {cell}
                      </div>
                    );
                  }
                  return (
                    <div key={j} className="flex items-start justify-between gap-2 py-2.5 first:pt-0 last:pb-0">
                      <div className="text-[11px] font-bold text-muted uppercase tracking-wider flex-shrink-0 w-28 pt-0.5">
                        {cols[j]}
                      </div>
                      <div className="text-xs text-slate-700 dark:text-slate-300 flex-1 min-w-0 break-words flex justify-end text-right">
                        {cell}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))
        }
        {footer && (
          <div className="px-3 sm:px-4 py-3 border border-slate-200 dark:border-slate-700/60 rounded-xl bg-slate-50/50 dark:bg-slate-800/40 shadow-xs overflow-hidden">{footer}</div>
        )}
      </div>
    );
  }

  return (
    <div className={`${horizontalScroll ? "overflow-x-auto -mx-5 px-5" : "overflow-x-hidden"}`}>
      <table className={`w-full text-sm ${horizontalScroll ? "min-w-max" : "min-w-full"}`}>
        <thead>
          <tr className="border-b border-border dark:border-slate-700">
            {cols.map((c, i) => <th key={i} className={`${pad} text-left text-[11px] font-bold text-muted dark:text-slate-300 uppercase tracking-wider whitespace-nowrap`}>{c}</th>)}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-50 dark:divide-slate-700/50">
          {loading ? (
            [1, 2, 3].map(i => (
              <tr key={i}>
                {cols.map((_, j) => (
                  <td key={j} className={pad}>
                    <div className="skeleton h-4 w-full rounded" />
                  </td>
                ))}
              </tr>
            ))
          ) : rows.length === 0
            ? <tr><td colSpan={cols.length} className="py-12 text-center text-muted dark:text-slate-400 text-sm">{displayEmptyMsg}</td></tr>
            : rows.map((row, i) => (
              <tr key={i} className="hover:bg-slate-50/80 dark:hover:bg-slate-800/60 transition-colors group">
                {row.map((cell, j) => <td key={j} className={`${pad} text-slate-700 dark:text-slate-300 align-middle whitespace-nowrap`}>{cell}</td>)}
              </tr>
            ))
          }
        </tbody>
        {footer && <tfoot><tr><td colSpan={cols.length} className="px-3.5 py-3 border-t border-border dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/40 rounded-b-xl">{footer}</td></tr></tfoot>}
      </table>
    </div>
  );
}
